import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Bell, Save, Send, Loader2, Calendar, FileText, AlertTriangle, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const FREQUENCIES = [
  { value: "MONTHLY", label: "Monthly" },
  { value: "QUARTERLY", label: "Quarterly" },
  { value: "HALF_YEARLY", label: "Half-yearly" },
  { value: "ANNUAL", label: "Annual" },
];

// Each report toggle is rendered identically — this small helper saves ~50
// lines of repeated JSX per toggle and keeps the spacing consistent.
const ReportToggle = ({ icon: Icon, title, description, value, onChange, testid }) => (
  <div className="flex items-start justify-between gap-3 p-3 rounded-lg border border-gray-200 bg-white">
    <div className="flex gap-3">
      <div className="w-9 h-9 rounded-md flex items-center justify-center bg-teal-50 text-teal-700 flex-shrink-0">
        <Icon className="w-4 h-4" />
      </div>
      <div className="min-w-0">
        <p className="text-sm font-medium text-gray-900">{title}</p>
        <p className="text-xs text-gray-500 mt-0.5">{description}</p>
      </div>
    </div>
    <Switch checked={value} onCheckedChange={onChange} data-testid={testid} />
  </div>
);

export default function ReportingPreferencesPanel({ api }) {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "super_admin" || user?.role === "broker";
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  // Fetch current global prefs
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await api.get("/reporting-preferences");
        setPrefs(r.data);
      } catch (e) {
        toast.error("Failed to load reporting preferences");
      } finally {
        setLoading(false);
      }
    })();
  }, [api]);

  const set = (k, v) => setPrefs((p) => ({ ...p, [k]: v }));

  const handleSave = async () => {
    if (!prefs) return;
    setSaving(true);
    try {
      const r = await api.put("/reporting-preferences", prefs);
      setPrefs(r.data);
      toast.success("Reporting preferences saved · applied to all clients");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save preferences");
    } finally {
      setSaving(false);
    }
  };

  const handleTestSend = async () => {
    if (!prefs?.test_recipient) {
      toast.error("Enter a test recipient email first");
      return;
    }
    setTesting(true);
    try {
      // Backend test-send endpoint will be wired up once delivery provider
      // (Gsuite SMTP / Resend / private) is configured. For now, surface a
      // clear message to the user.
      toast.info("Email delivery not yet configured", {
        description: "Test send will work once Gsuite SMTP / private mail relay credentials are saved in backend .env",
      });
    } catch (e) {
      toast.error("Test send failed");
    } finally {
      setTesting(false);
    }
  };

  if (loading || !prefs) {
    return (
      <div className="flex items-center justify-center py-20 text-gray-500 gap-2 text-sm">
        <Loader2 className="w-4 h-4 animate-spin" />
        Loading reporting preferences…
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="reporting-prefs-tab">
      {/* Master switch banner */}
      <Card className="border-teal-200 bg-gradient-to-br from-teal-50/60 to-emerald-50/40">
        <CardContent className="p-5 flex items-start justify-between gap-4">
          <div className="flex gap-3">
            <div className="w-12 h-12 rounded-xl bg-teal-700 text-white flex items-center justify-center flex-shrink-0">
              <Bell className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <h3 className="text-base font-semibold text-gray-900">Auto-send reports to clients</h3>
              <p className="text-sm text-gray-600 mt-1 max-w-xl">
                When ON, Kinntegra will automatically email the selected reports to <span className="font-medium">all clients</span> on the chosen schedule. Per-client opt-outs apply.
              </p>
              {prefs.updated_at && (
                <p className="text-xs text-gray-500 mt-2">
                  Last updated by <span className="font-medium">{prefs.updated_by || "unknown"}</span>
                  {prefs.updated_by_role ? ` (${prefs.updated_by_role})` : ""}
                  {" · "}{new Date(prefs.updated_at).toLocaleString("en-IN")}
                </p>
              )}
            </div>
          </div>
          <Switch
            checked={!!prefs.enabled}
            onCheckedChange={(v) => set("enabled", v)}
            data-testid="prefs-master-toggle"
            className="mt-1"
          />
        </CardContent>
      </Card>

      {/* Schedule */}
      <Card className="border-gray-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <Calendar className="w-4 h-4 text-teal-700" />
            Schedule
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label className="text-xs text-gray-600">Sending frequency</Label>
            <Select value={prefs.frequency} onValueChange={(v) => set("frequency", v)}>
              <SelectTrigger className="mt-1" data-testid="prefs-frequency">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FREQUENCIES.map((f) => (
                  <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-gray-600">Day of month (1–28)</Label>
            <Input
              type="number"
              min={1}
              max={28}
              value={prefs.send_day_of_month}
              onChange={(e) => set("send_day_of_month", Math.max(1, Math.min(28, Number(e.target.value) || 1)))}
              className="mt-1"
              data-testid="prefs-day"
            />
          </div>
          <div>
            <Label className="text-xs text-gray-600">Time (IST)</Label>
            <div className="flex items-center gap-1 mt-1">
              <Input
                type="number"
                min={0}
                max={23}
                value={prefs.send_time_hh}
                onChange={(e) => set("send_time_hh", Math.max(0, Math.min(23, Number(e.target.value) || 0)))}
                className="w-20"
                data-testid="prefs-hh"
              />
              <span className="text-gray-400">:</span>
              <Input
                type="number"
                min={0}
                max={59}
                value={prefs.send_time_mm}
                onChange={(e) => set("send_time_mm", Math.max(0, Math.min(59, Number(e.target.value) || 0)))}
                className="w-20"
                data-testid="prefs-mm"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Report toggles */}
      <Card className="border-gray-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <FileText className="w-4 h-4 text-teal-700" />
            Reports to include
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <ReportToggle
            icon={FileText}
            title="Monthly transactions"
            description="All purchases / redemptions / SIP installments processed in the period."
            value={prefs.monthly_transactions}
            onChange={(v) => set("monthly_transactions", v)}
            testid="prefs-toggle-monthly-txn"
          />
          <ReportToggle
            icon={ShieldCheck}
            title="Holdings / Portfolio summary"
            description="Current schemes, units, NAV, market value with P/L."
            value={prefs.holdings_summary}
            onChange={(v) => set("holdings_summary", v)}
            testid="prefs-toggle-holdings"
          />
          <ReportToggle
            icon={FileText}
            title="Capital Gains statement"
            description="Realised gains (FIFO + Grandfathered cost) for the period."
            value={prefs.capital_gains}
            onChange={(v) => set("capital_gains", v)}
            testid="prefs-toggle-capgains"
          />
          <ReportToggle
            icon={FileText}
            title="IDCW / Dividend statement"
            description="All dividend payouts received during the period."
            value={prefs.idcw_dividend}
            onChange={(v) => set("idcw_dividend", v)}
            testid="prefs-toggle-idcw"
          />
          <ReportToggle
            icon={AlertTriangle}
            title="SIP Bounce / Failed-transaction alerts"
            description="Triggered immediately when a mandate fails — separate from the periodic schedule."
            value={prefs.sip_bounce_alerts}
            onChange={(v) => set("sip_bounce_alerts", v)}
            testid="prefs-toggle-bounce"
          />
          <ReportToggle
            icon={Calendar}
            title="Financial Year (FY) consolidated report"
            description="Sent automatically at FY end (Mar 31) — full-year holdings + tax statements."
            value={prefs.fy_consolidated_enabled}
            onChange={(v) => set("fy_consolidated_enabled", v)}
            testid="prefs-toggle-fy"
          />
        </CardContent>
      </Card>

      {/* Sender details moved to Settings → Preferences. Test send stays here so any
          super-admin or associate can fire a test for the scope they manage. */}
      <Card className="border-gray-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <Send className="w-4 h-4 text-teal-700" />
            Test send
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col sm:flex-row gap-3">
          <Input
            type="email"
            placeholder="Enter your email to receive a test report"
            value={prefs.test_recipient}
            onChange={(e) => set("test_recipient", e.target.value)}
            className="max-w-md"
            data-testid="prefs-test-email"
          />
          <Button
            variant="outline"
            onClick={handleTestSend}
            disabled={testing || !prefs.test_recipient}
            data-testid="prefs-test-send"
          >
            {testing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
            Send test
          </Button>
        </CardContent>
      </Card>

      {/* Sticky footer save */}
      <div className="sticky bottom-3 z-20 flex justify-end pt-2">
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-teal-700 hover:bg-teal-800 text-white shadow-lg"
          data-testid="prefs-save"
        >
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save preferences for all clients
        </Button>
      </div>
    </div>
  );
}
